import { createContext, useContext, useEffect, useState } from 'react';
import { getSession, hasExpiredSession, saveSession, clearSession, SESSION_EXPIRED_EVENT } from '../../data/authSession';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getSession); // null means logged out
  // True when the user was logged out because the session expired (not by logging out),
  // so the login screen can say why it was opened.
  const [sessionExpired, setSessionExpired] = useState(hasExpiredSession);

  const login = (session) => {
    saveSession(session);
    setUser(session);
    setSessionExpired(false);
  };

  const logout = () => {
    clearSession();
    setUser(null);
    setSessionExpired(false);
  };

  const expire = () => {
    clearSession();
    setUser(null);
    setSessionExpired(true);
  };

  // apiClient fires this when the API rejects the token (expired or invalid).
  useEffect(() => {
    window.addEventListener(SESSION_EXPIRED_EVENT, expire);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, expire);
  }, []);

  // Expire the session when the token's expiry time is reached, even without any request.
  // If session has expired, and page is protected by ProtectedRoute, login page would be shown.
  useEffect(() => {
    if (!user) return;
    const timeout = setTimeout(expire, user.exp - Date.now());
    return () => clearTimeout(timeout);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, isLoggedIn: !!user, sessionExpired, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
